/**
 * BottomDrawer
 *
 * Universal bottom drawer component with 3 states: collapsed, preview, and full.
 * Supports tabs for different content views and drag-to-resize functionality.
 *
 * Usage:
 * ```tsx
 * const tabs = [
 *   { id: "cashflow", label: "Cash Flow", content: <CashFlowTable /> },
 *   { id: "taxyear", label: "Tax Year", content: <TaxYearTable /> }
 * ];
 *
 * <BottomDrawer
 *   state="preview"
 *   tabs={tabs}
 *   activeTabId="cashflow"
 *   onStateChange={setState}
 *   onTabChange={setActiveTab}
 * />
 * ```
 */

import { Box, Button, HStack, Text } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';

export type DrawerState = 'collapsed' | 'preview' | 'full';

export interface DrawerTab<T extends string = string> {
  /** Unique identifier for the tab */
  id: T;

  /** Display label for the tab */
  label: string;

  /** Render function - only called when tab is active (prevents unnecessary rendering) */
  render: () => React.ReactNode;

  /** Optional badge count to display */
  badgeCount?: number;
}

export interface BottomDrawerProps<T extends string = string> {
  /** Current drawer state */
  state: DrawerState;

  /** Available tabs */
  tabs: DrawerTab<T>[];

  /** Currently active tab ID */
  activeTabId: T;

  /** Handler called when drawer state changes */
  onStateChange: (state: DrawerState) => void;

  /** Handler called when active tab changes */
  onTabChange: (tabId: T) => void;

  /** Optional minimum height for full state (px) */
  minHeight?: number;

  /** Optional maximum height for full state (px) */
  maxHeight?: number;

  /** Optional preview height (px) */
  previewHeight?: number;

  /** Optional collapsed height (px) */
  collapsedHeight?: number;

  /** Optional message to show in preview state (null = show content, undefined = use default) */
  previewMessage?: string | null;

  /** Optional custom action buttons to replace default expand/collapse buttons */
  customActions?: React.ReactNode;

  /** Optional index at which to insert a visual separator between tab groups */
  tabSeparatorIndex?: number;
}

export const BottomDrawer = <T extends string = string>({
  state,
  tabs,
  activeTabId,
  onStateChange,
  onTabChange,
  minHeight = LAYOUT_SPACING.DRAWER_FULL_MIN,
  maxHeight = LAYOUT_SPACING.DRAWER_FULL_MAX,
  previewHeight = LAYOUT_SPACING.DRAWER_PREVIEW,
  collapsedHeight = LAYOUT_SPACING.DRAWER_COLLAPSED,
  previewMessage = 'Drag up or click expand to see full data',
  customActions,
  tabSeparatorIndex,
}: BottomDrawerProps<T>) => {
  const [drawerHeight, setDrawerHeight] = useState(maxHeight);
  const [isDragging, setIsDragging] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(maxHeight);

  // Update drawerHeight when state changes
  useEffect(() => {
    if (state === 'full') {
      setDrawerHeight(maxHeight);
    } else if (state === 'preview') {
      setDrawerHeight(previewHeight);
    }
  }, [state, maxHeight, previewHeight]);

  // Handle dragging to resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaY = startYRef.current - e.clientY; // Inverted: dragging up increases height

      // Set min/max based on current state
      const effectiveMin = state === 'preview' ? collapsedHeight : minHeight;
      const effectiveMax = maxHeight;

      const newHeight = Math.max(
        effectiveMin,
        Math.min(effectiveMax, startHeightRef.current + deltaY)
      );
      setDrawerHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsDragging(false);

      // Auto-switch states based on final height
      const viewportHeight = window.innerHeight;
      const currentHeightPercent = (drawerHeight / viewportHeight) * 100;

      if (state === 'preview') {
        // In preview mode, check if we should switch to full or collapsed
        if (currentHeightPercent > 75) {
          // Dragged near top - switch to full
          onStateChange('full');
        } else if (currentHeightPercent < 25) {
          // Dragged near bottom - collapse
          onStateChange('collapsed');
        } else {
          // Stay in preview, snap to preview height
          setDrawerHeight(previewHeight);
        }
      } else if (state === 'full') {
        // In full mode, check if we should switch to preview
        if (currentHeightPercent < 60) {
          // Dragged down significantly - switch to preview
          onStateChange('preview');
        }
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [
    isDragging,
    minHeight,
    maxHeight,
    state,
    drawerHeight,
    onStateChange,
    previewHeight,
    collapsedHeight,
  ]);

  const handleDragStart = (e: React.MouseEvent) => {
    setIsDragging(true);
    startYRef.current = e.clientY;

    // Set starting height based on current state
    if (state === 'preview') {
      startHeightRef.current = previewHeight;
    } else {
      startHeightRef.current = drawerHeight;
    }
  };

  // Get active tab content
  const activeTab = tabs.find((tab) => tab.id === activeTabId);

  // Collapsed state: show tabs as buttons
  if (state === 'collapsed') {
    return (
      <HStack
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        height={`${collapsedHeight}px`}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        justify="space-between"
        px={4}
        gap={LAYOUT_SPACING.BUTTON_GAP_LG}
        transition={`all ${LAYOUT_SPACING.TRANSITION_FAST}`}
        zIndex={100}
      >
        {/* Tab buttons */}
        <HStack gap={LAYOUT_SPACING.BUTTON_GAP}>
          {tabs.map((tab, index) => (
            <React.Fragment key={tab.id}>
              {tabSeparatorIndex !== undefined && index === tabSeparatorIndex && (
                <Box width="1px" height="20px" bg="gray.300" mx={2} flexShrink={0} />
              )}
              <Button
                size="sm"
                variant={activeTabId === tab.id ? 'solid' : 'ghost'}
                onClick={(e) => {
                  e.stopPropagation();
                  onTabChange(tab.id);
                  onStateChange('preview');
                }}
              >
                {tab.label}
                {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
                  <Box
                    as="span"
                    ml={1}
                    px={1.5}
                    py={0.5}
                    borderRadius="full"
                    bg="blue.500"
                    color="white"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    {tab.badgeCount}
                  </Box>
                )}
              </Button>
            </React.Fragment>
          ))}
        </HStack>

        {/* Custom actions (if provided) */}
        {customActions && <HStack gap={LAYOUT_SPACING.BUTTON_GAP}>{customActions}</HStack>}
      </HStack>
    );
  }

  // Calculate drawer height based on state and dragging
  const getDrawerHeight = (drawerState: DrawerState): number => {
    if (drawerState === 'collapsed') return collapsedHeight;
    if (drawerState === 'preview') return isDragging ? drawerHeight : previewHeight;
    return drawerHeight; // full mode
  };

  const height = `${getDrawerHeight(state)}px`;

  return (
    <Box
      ref={drawerRef}
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      height={height}
      bg="white"
      borderTop="1px solid"
      borderColor="gray.300"
      shadow="lg"
      transition={isDragging ? 'none' : `height ${LAYOUT_SPACING.TRANSITION_FAST}`}
      display="flex"
      flexDirection="column"
      zIndex={100}
    >
      {/* Drag handle (visible in preview and full states) */}
      {(state === 'preview' || state === 'full') && (
        <Box
          height="8px"
          bg="gray.200"
          cursor="ns-resize"
          onMouseDown={handleDragStart}
          _hover={{ bg: 'gray.300' }}
          transition={`background ${LAYOUT_SPACING.TRANSITION_FAST}`}
        />
      )}

      {/* Header with tabs and controls */}
      <HStack justify="space-between" p={3} borderBottom="1px solid" borderColor="gray.200">
        <HStack gap={LAYOUT_SPACING.BUTTON_GAP}>
          {tabs.map((tab, index) => (
            <React.Fragment key={tab.id}>
              {tabSeparatorIndex !== undefined && index === tabSeparatorIndex && (
                <Box width="1px" height="24px" bg="gray.300" mx={2} flexShrink={0} />
              )}
              <Button
                size="sm"
                variant={activeTabId === tab.id ? 'solid' : 'ghost'}
                onClick={() => onTabChange(tab.id)}
              >
                {tab.label}
                {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
                  <Box
                    as="span"
                    ml={2}
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    bg="blue.500"
                    color="white"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    {tab.badgeCount}
                  </Box>
                )}
              </Button>
            </React.Fragment>
          ))}
        </HStack>

        {customActions && <HStack gap={LAYOUT_SPACING.BUTTON_GAP}>{customActions}</HStack>}
      </HStack>

      {/* Content area */}
      <Box p={state === 'preview' ? 3 : LAYOUT_SPACING.CARD_PADDING} overflow="auto" flex={1}>
        {(() => {
          // Determine if we should show message (only if string with content)
          const shouldShowMessage =
            state === 'preview' && typeof previewMessage === 'string' && previewMessage.length > 0;

          return shouldShowMessage ? (
            <Text fontSize="xs" color="gray.500">
              {previewMessage}
            </Text>
          ) : (
            // Only render the active tab's content (lazy rendering)
            activeTab?.render() || <Text color="gray.500">No content available</Text>
          );
        })()}
      </Box>
    </Box>
  );
};
